import {
  Button,
  Card,
  TextInputField,
  TextAreaField,
  Banner,
  GreyWhite,
  Primary14SemiBold,
  GreyGrey60,
  Primary13Regular,
  Box,
  Flex,
  Text
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import Spinner from 'components/Loading/Spinner'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api/utils'
import { useEffect, useState, useCallback, useRef } from 'react'
import { dequal } from 'dequal'
import {
  NetsuiteAuthenticationFields,
  NetsuiteTestedSettings
} from 'Integrations/integrationsConfig/netsuite/netsuite.integration.config'
import { MaskedTextInputField } from 'Integrations/integrationsConfig/netsuite/NetsuiteConfigurationManagement/cards/MaskedTextInputField'
import { match } from 'ts-pattern'

type Props = {
  existingAuthentication: NetsuiteAuthenticationFields
  isConnected: boolean
  areSettingsValid: boolean
  // Only the settings the test payload carries: the item ids are not part of
  // minting a token, so editing one must not invalidate a passed test.
  settings: NetsuiteTestedSettings
  showValidationErrors: boolean
  onConnectionSuccess: () => void
  onAuthenticationChange: (authentication: NetsuiteAuthenticationFields) => void
}

const NetsuiteAuthenticationCard = ({
  existingAuthentication,
  isConnected,
  areSettingsValid,
  settings,
  showValidationErrors,
  onConnectionSuccess,
  onAuthenticationChange
}: Props) => {
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null)

  const { fields, queries } = useForm({
    value: existingAuthentication,
    showValidationErrors,
    fieldConfiguration: [
      { property: 'netsuiteAccountId', validation: [required] },
      { property: 'consumerKey', validation: [required] },
      { property: 'certificateId', validation: [required] },
      // The stored key is never returned, so a connected account starts with a
      // blank field and keeps the key it already has unless one is entered.
      { property: 'privateKey', validation: isConnected ? [] : [required] }
    ],
    onChange: onAuthenticationChange
  })

  const {
    isPending: isTestingCredentials,
    reset: resetTestCredentials,
    mutate: testCredentials
  } = useMutation(dashboardv99990101Client.postTestNetsuiteCredentials, {
    onSuccess: result => {
      if (result?.isValid) {
        setTestResult('success')
        onConnectionSuccess()
        return
      }
      setTestResult('error')
    },
    onError: () => {
      setTestResult('error')
    }
  })

  // Clear any stale test state for an already-connected account on mount.
  useEffect(() => {
    if (isConnected) {
      resetTestCredentials()
    }
  }, [isConnected, resetTestCredentials])

  const resetTestState = useCallback(() => {
    if (testResult) {
      resetTestCredentials()
      setTestResult(null)
    }
  }, [testResult, resetTestCredentials])

  // A test validates the settings too, so an edit to them makes the banner stale.
  const previousSettings = useRef(settings)
  useEffect(() => {
    if (!dequal(previousSettings.current, settings)) {
      previousSettings.current = settings
      resetTestState()
    }
  })

  const handleTextInputChange = useCallback(
    (onFieldChange: (value: string) => void) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        resetTestState()
        onFieldChange(event.target.value)
      },
    [resetTestState]
  )

  const handlePrivateKeyChange = useCallback(
    (value: string) => {
      resetTestState()
      fields.privateKey.onChange(value)
    },
    [resetTestState, fields.privateKey]
  )

  const handleTestCredentials = useCallback(() => {
    void testCredentials({
      netsuiteAccountId: fields.netsuiteAccountId.value,
      consumerKey: fields.consumerKey.value,
      certificateId: fields.certificateId.value,
      privateKey: fields.privateKey.value,
      subsidiaryId: settings.subsidiaryId,
      defaultItemIncomeAccountId: settings.defaultItemIncomeAccountId,
      defaultTaxScheduleId: settings.defaultTaxScheduleId
    })
  }, [fields, settings, testCredentials])

  // The test endpoint takes the whole configuration, private key included, so
  // it needs the settings filled in and a key entered even when one is stored.
  const canTestConfiguration =
    queries.isValid && areSettingsValid && Boolean(fields.privateKey.value)

  return (
    <Card data-testid="netsuiteAuth.card">
      <Flex flexDirection="column" gap="24px">
        <Box>
          <Text {...Primary14SemiBold}>Connect to NetSuite</Text>
          <Text {...Primary13Regular} mt="8px" color={GreyGrey60}>
            Enter your NetSuite credentials to connect your account.
          </Text>
        </Box>

        <Flex flexDirection="column" gap="16px">
          <TextInputField
            label="NetSuite account ID"
            data-testid="netsuiteAuth.netsuiteAccountId"
            value={fields.netsuiteAccountId.value}
            onChange={handleTextInputChange(fields.netsuiteAccountId.onChange)}
            validationErrors={fields.netsuiteAccountId.validationErrors}
            fullWidth
            autoComplete="off"
            disabled={isTestingCredentials}
          />

          <MaskedTextInputField
            label="Consumer key"
            data-testid="netsuiteAuth.consumerKey"
            value={fields.consumerKey.value}
            onChange={handleTextInputChange(fields.consumerKey.onChange)}
            validationErrors={fields.consumerKey.validationErrors}
            disabled={isTestingCredentials}
          />

          <MaskedTextInputField
            label="Certificate ID"
            data-testid="netsuiteAuth.certificateId"
            value={fields.certificateId.value}
            onChange={handleTextInputChange(fields.certificateId.onChange)}
            validationErrors={fields.certificateId.validationErrors}
            disabled={isTestingCredentials}
          />

          <TextAreaField
            label="Private key"
            data-testid="netsuiteAuth.privateKey"
            description={
              isConnected
                ? 'Leave this blank to keep the private key you already have. Enter a new one to re-authenticate.'
                : undefined
            }
            value={fields.privateKey.value}
            onChange={handlePrivateKeyChange}
            validationErrors={fields.privateKey.validationErrors}
            fontFamily="mono"
            height="100px"
            autoComplete="off"
            isDisabled={isTestingCredentials}
          />
        </Flex>

        <Box>
          <Button
            data-testid="netsuiteAuth.testCredentials"
            onClick={handleTestCredentials}
            disabled={isTestingCredentials || !canTestConfiguration}
            leadingIcon={
              isTestingCredentials ? (
                <Spinner color={GreyWhite} height="16px" width="16px" />
              ) : undefined
            }
            variant="primary"
          >
            {isTestingCredentials ? 'Checking...' : 'Test configuration'}
          </Button>
        </Box>

        {testResult &&
          match(testResult)
            .with('success', () => {
              return (
                <Banner
                  data-testid="netsuiteAuth.banner.success"
                  variant="success"
                >
                  Your NetSuite configuration has been validated
                </Banner>
              )
            })
            .with('error', () => {
              return (
                <Banner data-testid="netsuiteAuth.banner.error" variant="error">
                  This configuration is invalid. Check these details and try
                  again.
                </Banner>
              )
            })
            .exhaustive()}
      </Flex>
    </Card>
  )
}

export default NetsuiteAuthenticationCard
